/* eslint-disable */
/**
 * Land-page assets that were reaching the player only on demand, because they were never in the
 * blob preload manifest at all — so no amount of reclassifying could have warmed them.
 *
 * All three sets are gated (see `GATE_PATH_PREFIXES` / `GATE_EXACT_URLS` in
 * `vercelBlobPreloadUrls.ts`): they are reachable within a click or two of landing, and a missing
 * image in any of them reads as a broken screen rather than a slow one.
 */

const BLOB = 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com'

/**
 * Worker / character card chrome — `CharacterRarityCard.tsx`.
 *
 * NOTE: the worker and character PORTRAITS are not listed here and cannot be. They come from
 * per-record `characterFrontView` / `characterImg` values served by the API, so there is no static
 * URL set to warm. What is preloadable is the card furniture drawn around every portrait — frame,
 * divider, element badge, star — which is what makes a grid of workers pop in as a unit instead of
 * assembling itself piece by piece. The portraits themselves stream in and are cached by
 * `public/sw.js` on first use.
 */
export const CHARACTER_CARD_PRELOAD_URLS: readonly string[] = [
  `${BLOB}/characters_portal/character_card/common-frame.webp`,
  `${BLOB}/characters_portal/character_card/rare-frame.webp`,
  `${BLOB}/characters_portal/character_card/legendary-frame.webp`,
  `${BLOB}/characters_portal/character_card/common-divider.webp`,
  `${BLOB}/characters_portal/character_card/rare-divider.webp`,
  `${BLOB}/characters_portal/character_card/legendary-divider.webp`,
  `${BLOB}/characters_portal/element/fire.webp`,
  `${BLOB}/characters_portal/element/water.webp`,
  `${BLOB}/characters_portal/element/earth.webp`,
  `${BLOB}/characters_portal/element/lightning.webp`,
  `${BLOB}/characters_portal/element/magic.webp`,
  `${BLOB}/characters_portal/element/physic.webp`,
  `${BLOB}/characters_portal/element/sun.webp`,
  `${BLOB}/characters_portal/element/dark.webp`,
  `${BLOB}/_merged_/Eclipsera/Images/Inventory/star.webp`,
]

/**
 * Guide book — `components/GuideBook/*`. Only the images those tabs actually reference.
 *
 * Deliberately NOT the whole `_merged_/Eclipsera/Maps/` folder: that is 73 files / 17.6 MB, of
 * which the guide uses six. Gating the folder would have more than doubled the startup budget to
 * warm artwork the guide never shows.
 */
export const GUIDE_BOOK_PRELOAD_URLS: readonly string[] = [
  `${BLOB}/redesign/guide-book-bg.webp`,
  `${BLOB}/main/guide-book.webp`,
  `${BLOB}/main/next.webp`,
  `${BLOB}/main/prev.webp`,
  `${BLOB}/land/rounds.webp`,
  `${BLOB}/_merged_/Eclipsera/Maps/land.webp`,
  `${BLOB}/_merged_/Eclipsera/Maps/map-preview.webp`,
  `${BLOB}/_merged_/Eclipsera/Maps/serpent_tower.webp`,
  `${BLOB}/_merged_/Eclipsera/Maps/Regions/region1.webp`,
  `${BLOB}/_merged_/Eclipsera/Maps/Regions/region2.webp`,
  `${BLOB}/_merged_/Eclipsera/Maps/Regions/region3.webp`,
  `${BLOB}/_merged_/Eclipsera/castles/bararians.webp`,
  /**
   * The guide's 14 building illustrations are NOT listed here on purpose.
   * `LandPageGuideTab.tsx` points them at `_merged_/buildings/live/<name>/lvl1.webp`, and every
   * one of those returns HTTP 404 from the CDN — they are already broken in the running game,
   * independent of preloading. Preloading a 404 just burns three retries per URL in the warm
   * loop. Restore them here once the paths are fixed; see the note in the handover.
   */
]

/**
 * Upgrade / restore modal — `components/Modal/BuildingLevelUpModal.tsx`.
 * `chest-1..3` cycle by `((level - 1) % 3) + 1` for the no-building-type case.
 */
export const UPGRADE_RESTORE_MODAL_PRELOAD_URLS: readonly string[] = [
  `${BLOB}/redesign/upgrade-restore_modal/background.webp`,
  `${BLOB}/redesign/upgrade-restore_modal/logo.webp`,
  `${BLOB}/redesign/upgrade-restore_modal/card1.webp`,
  `${BLOB}/redesign/upgrade-restore_modal/card2.webp`,
  `${BLOB}/eclipsera-gem.webp`,
  `${BLOB}/utils/chest-1.webp`,
  `${BLOB}/utils/chest-2.webp`,
  `${BLOB}/utils/chest-3.webp`,
]

/**
 * Connect / start page — the first screens in the game, before the player can do anything.
 *
 * These were absent from the manifest entirely, so the very first thing anyone sees was also the
 * least prepared. The whole set is 0.5 MB.
 *
 * Two assets referenced by `StartPage.tsx` are deliberately NOT listed because they 404 on the
 * CDN and are therefore already blank in the running game:
 *   `_merged_/Eclipsera/StartPage/logo_text.webp`  (StartPage.tsx:386)
 *   `redesign/language/footer.webp`                (StartPage.tsx:44)
 * Add them here once the artwork is published.
 */
export const LOGIN_START_PAGE_PRELOAD_URLS: readonly string[] = [
  // Shared by connectPage.jsx (top bar) and StartPage.tsx.
  `${BLOB}/land/logo.png`,
  // Language picker on the start page.
  `${BLOB}/redesign/language/body.webp`,
  `${BLOB}/redesign/language/header.webp`,
  `${BLOB}/redesign/language/row.webp`,
  `${BLOB}/redesign/language/row-active.webp`,
]

/** Everything above, for the manifest. */
export const LAND_GATE_EXTRAS_PRELOAD_URLS: readonly string[] = [
  ...CHARACTER_CARD_PRELOAD_URLS,
  ...GUIDE_BOOK_PRELOAD_URLS,
  ...UPGRADE_RESTORE_MODAL_PRELOAD_URLS,
  ...LOGIN_START_PAGE_PRELOAD_URLS,
]
